import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { getRequest } from '../shared/components/functions';
import { signOut } from '../utils/auth';

export const useAuthStore = create((set) => ({
  user: null,
  setUser: (user) => set({ user }),
  errorMessage: null,
  isLoading: false,
  // full-page sign-out through Microsoft (utils/auth.js)
  logout: () => {
    set({ user: null });
    signOut();
  }
}));

// Lookups only ever hold lists: an error body (a 401 while the session is renewed with Microsoft) must not replace the
// stored list, or pages using .find / .map crash on their first render.
const onlyLists = (values) => Object.fromEntries(Object.entries(values || {}).filter(([, value]) => Array.isArray(value)));
const setList = (set, values) => {
  const lists = onlyLists(values);
  if (Object.keys(lists).length) set(lists);
};

export const useLookupStore = create(
  persist(
    (set) => ({
      agreementtypes: [],
      setAgreementtypes: async () => setList(set, { agreementtypes: await getRequest(`/api/agreement_types`) }),
      companystatuses: [],
      setCompanystatuses: async () => setList(set, { companystatuses: await getRequest(`/api/companystatuses`) }),
      companytags: [],
      setCompanytags: async () => setList(set, { companytags: await getRequest(`/api/companytags`) }),
      contacttitels: [],
      setContacttitels: async () => setList(set, { contacttitels: await getRequest(`/api/contacttitels`) }),
      countries: [],
      setCountries: async () => setList(set, { countries: await getRequest(`/api/countries`) }),
      currencies: [],
      setCurrencies: async () => setList(set, { currencies: await getRequest(`/api/currencies`) }),
      documenttypes: [],
      setDocumenttypes: async () => setList(set, { documenttypes: await getRequest(`/api/documenttypes`) }),
      emailformats: [],
      setEmailformats: async () => setList(set, { emailformats: await getRequest(`/api/emailformats`) }),
      incasseren: [],
      setIncasseren: async () => setList(set, { incasseren: await getRequest(`/api/incasseren`) }),
      invoicestatuses: [],
      setInvoicestatuses: async () => setList(set, { invoicestatuses: await getRequest(`/api/invoicestatuses`) }),
      languages: [],
      setLanguages: async () => setList(set, { languages: await getRequest(`/api/languages`) }),
      machinebrands: [],
      setMachinebrands: async () => setList(set, { machinebrands: await getRequest(`/api/machinebrands`) }),
      machinetypes: [],
      setMachinetypes: async () => setList(set, { machinetypes: await getRequest(`/api/machinetypes`) }),
      organisations: [],
      setOrganisations: async () => setList(set, { organisations: await getRequest(`/api/organisations`) }),
      paymentconditions: [],
      setPaymentconditions: async () => setList(set, { paymentconditions: await getRequest(`/api/paymentconditions`) }),
      pickingplaces: [],
      setPickingplaces: async () => setList(set, { pickingplaces: await getRequest(`/api/pickingplaces`) }),
      projectstatuses: [],
      setProjectstatuses: async () => setList(set, { projectstatuses: await getRequest(`/api/projectstatuses`) }),
      businessunits: [],
      setBusinessunits: async () => setList(set, { businessunits: await getRequest(`/api/business_units/active`) }),
      pubcodes: [],
      setPubcodes: async () => setList(set, { pubcodes: await getRequest(`/api/pubcodes`) }),
      purchasetypes: [],
      setPurchasetypes: async () => setList(set, { purchasetypes: await getRequest(`/api/purchasetypes`) }),
      quotationstatuses: [],
      setQuotationstatuses: async () => setList(set, { quotationstatuses: await getRequest(`/api/quotationstatuses`) }),
      rappeltexts: [],
      setRappeltexts: async () => setList(set, { rappeltexts: await getRequest(`/api/rappeltexts`) }),
      sectors: [],
      setSectors: async () => setList(set, { sectors: await getRequest(`/api/sectors`) }),
      servicecontracttypes: [],
      setServicecontracttypes: async () => setList(set, { servicecontracttypes: await getRequest(`/api/service_contracttypes`) }),
      units: [],
      setUnits: async () => setList(set, { units: await getRequest(`/api/units`) }),
      users: [],
      setUsers: async () => {
        const users = await getRequest(`/api/users`);
        if (!Array.isArray(users)) return;
        set({ users });
        // Also populate internal users
        const internalUsers = users.filter((user) => {
          const isActive = user.Inactief === 0 || user.Inactief === false;
          const hasEmail = user.Email && user.Email !== '';
          const isNotTechnician = user.UserType !== 4;
          return isActive && hasEmail && isNotTechnician;
        });
        set({ internalUsers });
      },
      internalUsers: [],
      setInternalUsers: async () => {
        const allUsers = await getRequest(`/api/users`);
        if (!Array.isArray(allUsers)) return;
        const internalUsers = allUsers.filter((user) => user.Inactief === 0 && user.Email && user.Email !== '');
        set({ internalUsers });
      },
      usertypes: [],
      setUsertypes: async () => setList(set, { usertypes: await getRequest(`/api/usertypes`) }),
      vatshifts: [],
      setVatshifts: async () => setList(set, { vatshifts: await getRequest(`/api/vatshifts`) }),
      zipcodes: [],
      setZipcodes: async () => setList(set, { zipcodes: await getRequest(`/api/zipcodes`) }),
      producttypes: [],
      setProducttypes: async () => setList(set, { producttypes: await getRequest(`/api/product_types`) }),
      overheadpercentages: [],
      setOverheadpercentages: async () => setList(set, { overheadpercentages: await getRequest(`/api/overhead_percentages`) })
    }),
    {
      name: 'lookup-storage',
      // also drops what older versions stored when a lookup failed
      merge: (persisted, current) => ({ ...current, ...onlyLists(persisted) })
    }
  )
);

// pending approval counts for the navigation badge and the homepage card (refreshed by PageWrapper and the queue pages)
export const useApprovalSummaryStore = create((set) => ({
  summary: null,
  loadedAt: null,
  refresh: async () => {
    const summary = await getRequest('/api/approvals/summary');
    set({ summary: summary && typeof summary === 'object' && !Array.isArray(summary) ? summary : null, loadedAt: Date.now() });
  }
}));
